import type { ComponentProps, HTMLAttributes, ReactNode, Ref } from 'react';
import { HeadingContext } from 'react-aria-components/Heading';
import { DEFAULT_SLOT, Provider } from 'react-aria-components/slots';
import { TextContext } from 'react-aria-components/Text';
import { VisuallyHidden } from 'react-aria/VisuallyHidden';
import { useControlledState } from '@react-stately/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

import type { IconName } from '@launchpad-ui/icons';
import { Icon, StatusIcon } from '@launchpad-ui/icons';

import { ButtonGroupContext } from './ButtonGroup';
import { IconButton } from './IconButton';

import styles from './styles/Alert.module.css';

interface AlertTextProps extends ComponentProps<'div'> {
	ref?: Ref<HTMLDivElement>;
}

/**
 * AlertText wraps the title and description content within an Alert.
 * Use this to group Heading and Text elements when using actionsLayout="inline".
 */
const AlertText = ({ className, ref, ...props }: AlertTextProps) => {
	return <div ref={ref} className={`${styles.text} ${className ?? ''}`.trim()} {...props} />;
};

const alertStyles = cva(styles.base, {
	variants: {
		status: {
			neutral: null,
			error: styles.error,
			info: styles.info,
			success: styles.success,
			warning: styles.warning,
		},
		variant: {
			default: styles.default,
			inline: styles.inline,
		},
		actionsLayout: {
			stacked: null,
			inline: styles.actionsInline,
		},
	},
	defaultVariants: {
		status: 'neutral',
		variant: 'default',
	},
});

// Wording mirrors StatusIcon's accessible names, without its trailing "icon".
const statusLabels = {
	error: 'Error',
	info: 'Info',
	success: 'Success',
	warning: 'Warning',
} as const;

interface AlertVariants extends VariantProps<typeof alertStyles> {}

interface AlertProps extends HTMLAttributes<HTMLDivElement>, AlertVariants {
	/**
	 * The severity being communicated, which sets the colour and the derived icon. `neutral` is for
	 * informational copy that carries no severity and gets no icon or announced status.
	 * @default 'neutral'
	 */
	status?: AlertVariants['status'];
	/**
	 * `default` is the block alert used at the top of a page or section. `inline` is the compact
	 * form for use inside a form, table row, or card; it ignores `actionsLayout`.
	 * @default 'default'
	 */
	variant?: AlertVariants['variant'];
	/**
	 * Whether actions sit below the content (`stacked`) or beside it (`inline`). Inline actions
	 * expect the heading and body to be grouped in an `AlertText`. Ignored unless
	 * `variant="default"`.
	 * @default 'stacked'
	 */
	actionsLayout?: 'stacked' | 'inline';
	/**
	 * The icon shown at the start of the alert. Omit it to derive the icon from `status`; a
	 * `neutral` alert derives none. Pass a name to override the derived icon, or on a `neutral`
	 * alert to give it one. Pass `null` for no icon at all.
	 */
	icon?: IconName | null;
	/** Whether the alert can be dismissed. */
	isDismissable?: boolean;
	/**
	 * Whether the alert is open. Pass it to take control of visibility — a controlled alert stays
	 * mounted when dismissed and only closes once you flip this. Omit it to let the alert remove
	 * itself on dismiss.
	 */
	isOpen?: boolean;
	/** Handler called when the alert is dismissed. */
	onDismiss?: () => void;
	ref?: Ref<HTMLDivElement>;
}

/**
 * An alert is an inline message that communicates a status a user needs to notice, optionally with
 * an icon, actions, and a dismiss control.
 *
 * Reach for something else when the message is transient and needs no acknowledgement
 * (`toastQueue` / `snackbarQueue`), when it must block the user until they respond (`Modal` with a
 * `Dialog`), or when it belongs to a single form field (`FieldError`).
 *
 * Compose the content as children: a `Heading`, a `Text`, and a `ButtonGroup` of actions. The alert
 * themes those slots, so keep every other prop on the children themselves. With
 * `actionsLayout="inline"`, group the heading and text in an `AlertText` so the actions can sit
 * beside them.
 *
 * The icon is derived from `status`. Pass `icon` to override it (or to give a `neutral` alert one),
 * or `icon={null}` for no icon; either way the status stays announced to screen readers.
 *
 * @example
 * ```tsx
 * <Alert status="warning" isDismissable>
 *   <Heading>Payment method expired</Heading>
 *   <Text>Update your card to avoid an interruption.</Text>
 *   <ButtonGroup>
 *     <Button variant="primary">Update card</Button>
 *   </ButtonGroup>
 * </Alert>
 * ```
 */
const Alert = ({
	className,
	children,
	status = 'neutral',
	variant = 'default',
	actionsLayout = 'stacked',
	isDismissable,
	isOpen,
	onDismiss,
	icon,
	ref,
	...props
}: AlertProps) => {
	const [open, setOpen] = useControlledState(isOpen, true, (val) => !val && onDismiss?.());

	const resolvedActionsLayout = variant === 'default' ? actionsLayout : undefined;

	// cva widens `status` to include null, and StatusIcon has no 'neutral' kind.
	const statusKind = status && status !== 'neutral' ? status : undefined;
	let resolvedIcon: ReactNode = null;
	if (icon) {
		resolvedIcon = <Icon name={icon} size="small" className={styles.icon} aria-hidden />;
	} else if (icon !== null && statusKind) {
		resolvedIcon = <StatusIcon size="small" kind={statusKind} className={styles.icon} />;
	}

	// StatusIcon names the status for screen readers. When `icon` overrides or suppresses it,
	// the status would otherwise be conveyed by colour alone.
	const statusLabel = statusKind && icon !== undefined ? statusLabels[statusKind] : undefined;

	return open ? (
		<div
			ref={ref}
			{...props}
			role="alert"
			className={alertStyles({
				status,
				variant,
				actionsLayout: resolvedActionsLayout,
				className,
			})}
		>
			{statusLabel && <VisuallyHidden>{statusLabel}</VisuallyHidden>}
			{resolvedIcon}
			<div className={styles.content}>
				<Provider
					values={[
						[HeadingContext, { className: styles.heading }],
						[ButtonGroupContext, { className: styles.buttonGroup }],
						[
							TextContext,
							{
								slots: {
									[DEFAULT_SLOT]: {},
									subtitle: {},
								},
							},
						],
					]}
				>
					{children}
				</Provider>
			</div>
			{isDismissable && (
				<IconButton
					aria-label="Close"
					icon="cancel"
					variant="minimal"
					size="medium"
					className={styles.close}
					onPress={() => setOpen(false)}
				/>
			)}
		</div>
	) : null;
};

export { Alert, AlertText, alertStyles };
export type { AlertProps, AlertTextProps };
